import React, { useEffect, useState } from 'react';

interface Reference {
  type: string;
  ref: string;
}

interface ArticleData {
  id: string;
  title: string;
  meta: string;
  contentHtml: string;
  sourceUrl?: string;
  sourceName?: string;
  references?: Reference[];
}

interface ArticleModalProps {
  id: string | null;
  onClose: () => void;
}

const ArticleModal: React.FC<ArticleModalProps> = ({ id, onClose }) => {
  const [article, setArticle] = useState<ArticleData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id) {
      setArticle(null);
      return;
    }

    setLoading(true);
    fetch(`/data/articles/${id}.json`)
      .then(response => response.json())
      .then((data: ArticleData) => {
        setArticle(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading article:', error);
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!id) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="article-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '40px 20px',
        overflowY: 'auto'
      }}
    >
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)'
        }}
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        style={{
          position: 'relative',
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          border: '1px solid var(--border)'
        }}
      >
        {/* Header */}
        <header
          style={{
            padding: '24px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            position: 'sticky',
            top: 0,
            backgroundColor: 'var(--bg-primary)',
            zIndex: 1
          }}
        >
          <div style={{ flex: 1 }}>
            <h3
              id="article-modal-title"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: '1.5rem',
                fontWeight: '600',
                color: 'var(--text-primary)',
                margin: 0,
                lineHeight: '1.3'
              }}
            >
              {article?.title || 'Loading...'}
            </h3>
            {article?.meta && (
              <p style={{ color: 'var(--text-secondary)', margin: '8px 0 0 0', fontSize: '14px' }}>
                {article.meta}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close article"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: '4px',
              marginLeft: '16px',
              borderRadius: '4px'
            }}
          >
            ×
          </button>
        </header>

        {/* Article Content */}
        <article
          style={{
            padding: '32px',
            color: 'var(--text-primary)',
            lineHeight: '1.7'
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: 'var(--text-secondary)' }}>Loading article...</p>
            </div>
          ) : article ? (
            <div 
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
              style={{
                fontSize: '17px',
                lineHeight: '1.7'
              }}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: 'var(--text-secondary)' }}>Article not found.</p>
            </div>
          )}
        </article>

        {/* Footer */}
        {article && (article.references || article.sourceUrl) && (
          <footer
            style={{
              padding: '24px',
              borderTop: '1px solid var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }}
          >
            {article.references && article.references.length > 0 && (
              <div style={{ marginBottom: article.sourceUrl ? '16px' : '0' }}>
                <h4 style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: 'var(--text-primary)',
                  marginBottom: '8px'
                }}>
                  References:
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {article.references.map((ref, index) => (
                    <span
                      key={index}
                      style={{
                        backgroundColor: 'var(--accent-primary)',
                        color: 'var(--bg-primary)',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      {ref.type === 'quran' ? 'Quran' : 'Hadith'} {ref.ref}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {article.sourceUrl && (
              <a
                href={article.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'var(--accent-primary)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                Read on {article.sourceName || 'original source'} →
              </a>
            )}
          </footer>
        )}
      </div>
    </div>
  );
};

export default ArticleModal;